import {
    Box,
    Button,
    Divider,
    Typography,
    LinearProgress,
    Tooltip,
    Card,
    CardContent,
    IconButton,
} from "@mui/material";
import {useEffect, useState, useRef, memo} from "react";
import {useHttp} from "../../hooks/useHttp";
import {TaskBatch, Task, TaskStatusType} from "../../models/tasks";
import {StatsMap, LayoutMode} from "../../pages/Tasks/Tasks";
import {TaskComponent} from "../TaskComponent/TaskComponent";
import "./BatchComponent.scss";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import {Image} from "../../models/images";

const lungs = require("../../images/lungs.png");
const background = require("../../images/background.png");

interface BatchComponentInterface {
    taskId: string;
    refresh: () => void;
    pipelineStats: StatsMap | null;
    layoutMode: LayoutMode;
    completedTasksNumber: number;
}

// Memoized component for time display to prevent parent re-renders
const TimeDisplay = memo(({ createdAt, completed }: { createdAt: string, completed: boolean }) => {
    const [timeAwaited, setTimeAwaited] = useState<string>("");
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const createdAtTime = new Date(createdAt).getTime();

        const updateTimeAwaited = () => {
            const now = new Date().getTime();
            const diff = now - createdAtTime;

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setTimeAwaited(`${hours}h ${minutes}m ${seconds}s`);
        };

        updateTimeAwaited();

        if (completed) return;

        timerRef.current = setInterval(updateTimeAwaited, 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [createdAt, completed]);

    return (
        <Typography variant="body1">{timeAwaited}</Typography>
    );
});

export const BatchComponent = ({
                                   taskId,
                                   refresh,
                                   pipelineStats,
                                   layoutMode,
                                   completedTasksNumber,
                               }: BatchComponentInterface) => {
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [activeTab, setActiveTab] = useState<"lungs" | "nodules">("lungs");
    const [batchProgress, setBatchProgress] = useState<number>(0);
    const [task, setTask] = useState<TaskBatch | null>(null);
    const [fullTaskView, setFullTaskView] = useState<boolean>(false);
    const [images, setImages] = useState<{ [id: string]: Image }>({});

    useEffect(() => {
        const imagesStr = sessionStorage.getItem("cachedImages");
        if (imagesStr === null) return;
        const cachedImages = JSON.parse(imagesStr);
        const imagesDict = cachedImages.reduce((acc: { [id: string]: Image }, image: Image) => {
            acc[image.id] = image;
            return acc;
        }, {});
        setImages(imagesDict);
    }, []);

    const {trigger: fetchImages} = useHttp<Image[]>("/images");

    useEffect(() => {
        const imagesStr = sessionStorage.getItem("cachedImages");
        if (imagesStr) {
            const cachedImages = JSON.parse(imagesStr);
            const imagesDict = cachedImages.reduce((acc: { [id: string]: Image }, image: Image) => {
                acc[image.id] = image;
                return acc;
            }, {});
            setImages(imagesDict);
        } else {
            fetchImages({
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    sessionId: sessionStorage.getItem("sessionId") || "",
                },
            }).then((response) => {
                if (response) {
                    setImages(response);
                    sessionStorage.setItem("cachedImages", JSON.stringify(response));
                }
            });
        }
    }, [fetchImages]);

    const {
        loading: fetched_task_loading,
        error: fetched_task_error,
        trigger: fetchTask,
    } = useHttp<TaskBatch>(`/batch/${taskId}`);

    const {trigger: fetchTaskCsv} = useHttp<TaskBatch>(`/batch/csv`);
    const {trigger: fetchTaskDownloadAll} = useHttp<TaskBatch>(`/batch/download-all`);

    useEffect(() => {
        fetchTask(`/batch/${taskId}`).then((r: TaskBatch) => {
            setTask(r);
        });
    }, [taskId, fetchTask, completedTasksNumber]);

    const calculateBatchProgress = () => {
        if (!task) return 0;
        const totalTasks = task.lungsTasks.length + task.nodulesTasks.length;
        if (totalTasks === 0) return 0;

        const completedTasks = [...task.lungsTasks, ...task.nodulesTasks].filter((t) => t.completed).length;
        return Math.round((completedTasks / totalTasks) * 100);
    };

    const bulkDownloadReports = async () => {
        const response: any = await fetchTaskDownloadAll({
            method: "GET",
            responseType: "blob",
            headers: {
                "Content-Type": "application/json",
            },
            params: {
                id: taskId,
            },
        });

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "reports.zip");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const downloadCsv = async () => {
        const response: any = await fetchTaskCsv({
            method: "GET",
            responseType: "blob",
            headers: {
                "Content-Type": "application/json",
            },
            params: {
                id: taskId,
            },
        });

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "results.csv");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (!task) return;
        setBatchProgress(calculateBatchProgress());
    }, [task]);

    useEffect(() => {
        if (!task) return;
        if (task.lungsTasks.length > 0) {
            setSelectedTask(task.lungsTasks[0]);
            setActiveTab("lungs");
        } else if (task.nodulesTasks.length > 0) {
            setSelectedTask(task.nodulesTasks[0]);
            setActiveTab("nodules");
        }
    }, [task, selectedTask]);

    if (!task) return null;

    return (
        <div className="batchComponent">
            {fullTaskView && selectedTask ? (
                <div className="batchComponent-fullTaskView">
                    <Box className="batchComponent-fullTaskView-header">
                        <IconButton
                            className="batchComponent-backButton"
                            onClick={() => setFullTaskView(false)}
                            aria-label="Back to batch view"
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                    <TaskComponent
                        task={selectedTask}
                        refresh={() => refresh()}
                        pipelineStats={pipelineStats}
                        layoutMode={layoutMode}
                    />
                </div>
            ) : (
                <>
                    <Box
                        className="batchComponent-header"
                        style={{
                            background: `url(${background})`,
                            filter: "hue-rotate(40deg)",
                        }}
                    >
                        <img src={lungs} alt="Batch Icon" className="batchComponent-header-icon"/>
                        <Typography variant={"h4"}>Batch Task</Typography>
                        <Divider
                            flexItem
                            style={{
                                width: "100%",
                                height: "1px",
                                backgroundColor: "white",
                                opacity: 0.2,
                                margin: ".5rem 0",
                            }}
                        />
                        <Typography variant={"h2"}>{task.name}</Typography>
                        {task.description && (
                            <Typography variant="body1" className="batchComponent-description">
                                {task.description}
                            </Typography>
                        )}
                    </Box>

                    <Box className="batchComponent-dashboard">
                        <div className="batchComponent-dashboard-grid">
                            <div className="batchComponent-dashboard-item">
                                <div className="batchComponent-dashboardCard">
                                    <Typography variant="h6">Batch Progress</Typography>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Box width="100%" mr={1}>
                                            <LinearProgress variant="determinate" value={batchProgress}/>
                                        </Box>
                                        <Box minWidth={35}>
                                            <Typography variant="body2"
                                                        color="textSecondary">{`${batchProgress}%`}</Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="textSecondary">
                                            {batchProgress === 100 ? (
                                                <Box display="flex" alignItems="center">
                                                    <CheckCircleOutlineIcon color="success" fontSize="small"
                                                                            style={{marginRight: "4px"}}/>
                                                    Completed
                                                </Box>
                                            ) : (
                                                <Box display="flex" alignItems="center">
                                                    <HourglassEmptyIcon color="primary" fontSize="small"
                                                                        style={{marginRight: "4px"}}/>
                                                    In Progress
                                                </Box>
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {`${[...task.lungsTasks, ...task.nodulesTasks].filter((t) => t.completed).length} of ${task.lungsTasks.length + task.nodulesTasks.length} tasks completed`}
                                        </Typography>
                                    </Box>
                                </div>
                            </div>

                            <div className="batchComponent-dashboard-item">
                                <div className="batchComponent-dashboardCard">
                                    <div>
                                        <Typography variant="h6" gutterBottom>
                                            Time Information
                                        </Typography>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <AccessTimeIcon color="primary" style={{marginRight: "8px"}}/>
                                            <Box display="flex" alignItems="center">
                                                <Typography variant="body1" style={{marginRight: "4px"}}>Time Awaited: </Typography>
                                                <TimeDisplay createdAt={task.createdAt} completed={task.completed} />
                                            </Box>
                                        </Box>
                                        <Box display="flex" alignItems="center">
                                            <Typography variant="body2" color="textSecondary">
                                                Created: {new Date(task.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </div>
                                </div>
                            </div>

                            <div className="batchComponent-dashboard-item">
                                <div className="batchComponent-buttons">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CloudDownloadIcon/>}
                                        onClick={bulkDownloadReports}
                                        disabled={!task.completed}
                                        className="batchComponent-button"
                                    >
                                        Bulk Download All Reports
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CloudDownloadIcon/>}
                                        onClick={downloadCsv}
                                        disabled={!task.completed}
                                        className="batchComponent-button"
                                    >
                                        Download CSV
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Box>

                    <div className="batchComponent-body">
                        <div className="batchComponent-tabs">
                            <Button
                                variant={activeTab === "lungs" ? "contained" : "outlined"}
                                onClick={() => setActiveTab("lungs")}
                                className={activeTab === "lungs" ? "active" : ""}
                                disabled={task.lungsTasks.length === 0}
                            >
                                Lungs Tasks ({task.lungsTasks.length})
                            </Button>
                            <Button
                                variant={activeTab === "nodules" ? "contained" : "outlined"}
                                onClick={() => setActiveTab("nodules")}
                                className={activeTab === "nodules" ? "active" : ""}
                                disabled={task.nodulesTasks.length === 0}
                            >
                                Nodules Tasks ({task.nodulesTasks.length})
                            </Button>
                        </div>

                        <Divider/>

                        <div className="batchComponent-tasks">
                            <div className="batchComponent-tasksList" style={{width: "100%"}}>
                                <div className="batchComponent-tasksGrid">
                                    {(activeTab === "lungs" ? task.lungsTasks : task.nodulesTasks).map((subTask) => (
                                        <div key={subTask.id} className="batchComponent-tasksGrid-item">
                                            <div
                                                className={`batchComponent-taskItem ${selectedTask?.id === subTask.id ? "selected" : ""}`}
                                                onClick={() => {
                                                    setSelectedTask(subTask);
                                                    setFullTaskView(true);
                                                }}
                                            >
                                                <img
                                                    src={images[subTask.image.id].base64Image}
                                                    alt={subTask.image.id}
                                                    className="batchComponent-taskImage"
                                                />
                                                <div className="batchComponent-taskInfo">
                                                    <Typography variant="body2" noWrap>
                                                        {subTask.name}
                                                    </Typography>
                                                    <Box display="flex" justifyContent="space-between"
                                                         alignItems="center">
                                                        <Typography variant="caption" color="textSecondary">
                                                            {new Date(subTask.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        mt={1}
                                                        className={`batchComponent-statusBadge ${
                                                            subTask.completed
                                                                ? "completed"
                                                                : subTask.statuses.length > 0 && subTask.statuses[subTask.statuses.length - 1].status === TaskStatusType.TERMINATED
                                                                    ? "failed"
                                                                    : "pending"
                                                        }`}
                                                    >
                                                        <Typography variant="caption">
                                                            {subTask.completed
                                                                ? "COMPLETED"
                                                                : subTask.statuses.length > 0 && subTask.statuses[subTask.statuses.length - 1].status === TaskStatusType.TERMINATED
                                                                    ? "FAILED"
                                                                    : "IN PROGRESS"}
                                                        </Typography>
                                                    </Box>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
